import React from 'react'
import clsx from 'clsx'
import { PageTitle } from '_metronic/layout/core'
import { WithChildren, KTSVG } from '_metronic/helpers'
import PageBreadCrumb from 'Presentation/Components/PageBreadCrumb'
import CustomTablePagination from 'Presentation/Components/Table/Components/CustomTablePagination'
import TableLoading from 'Presentation/Components/Table/Components/TableLoading'

import useViewModel from './ViewModel'
import DateRangePicker from './DateRangePicker'

const COLUMN_COUNT = 5

const HEADER_STYLE: React.CSSProperties = {
  color: '#475467',
  textTransform: 'none',
  paddingLeft: '1.25rem',
  paddingRight: '1.25rem',
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const Highlight: React.FC<{ text: string; term: string }> = ({ text, term }) => {
  const trimmed = term.trim()
  if (!trimmed) return <>{text}</>

  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, 'gi'))
  const lowerTerm = trimmed.toLowerCase()

  return (
    <>
      {parts.map((part, idx) =>
        part.toLowerCase() === lowerTerm ? (
          <mark key={idx} className='p-0 bg-warning'>
            {part}
          </mark>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        )
      )}
    </>
  )
}

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = React.useState(false)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button
      type='button'
      className={clsx('btn btn-sm btn-icon', copied ? 'btn-light-success' : 'btn-light')}
      onClick={onCopy}
      aria-label='Copy to clipboard'>
      <KTSVG
        path={
          copied
            ? '/media/icons/duotune/general/gen043.svg'
            : '/media/icons/duotune/general/gen054.svg'
        }
        className='svg-icon-2'
      />
    </button>
  )
}

const ExternalApiAuditLogListView: React.FC = () => {
  const {
    pagedData,
    isLoading,
    isError,
    page,
    pageSize,
    filteredCount,
    search,
    onSearchChange,
    statusFilter,
    statusOptions,
    onStatusFilterChange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    clearFilters,
    isExpanded,
    sortField,
    sortDirection,
    onSort,
    toggleExpand,
    goToPage,
    formatDate,
    prettyJson,
  } = useViewModel()

  const renderSortableHeader = (field: any, label: string) => {
    const isSorted = sortField === field
    return (
      <th
        className='pt-2 pb-3 position-relative cursor-pointer table-header-sortable'
        style={HEADER_STYLE}
        onClick={() => onSort(field)}>
        <span className='table-header-sortable-label'>{label}</span>
        {isSorted && (
          <span className='position-absolute right-0'>
            <KTSVG
              path={
                sortDirection === 'desc'
                  ? '/media/icons/duotune/arrows/arr004.svg'
                  : '/media/icons/duotune/arrows/arr003.svg'
              }
              className='svg-icon-2 svg-icon-primary'
            />
          </span>
        )}
      </th>
    )
  }

  return (
    <ViewPageTitle>
      <div className='row gy-5 mb-5'>
        <div className='col-12'>
          <PageBreadCrumb />
        </div>

        <div className='col-12'>
          <div className='card'>
            <div className='card-header'>
              <div className='card-title'>
                <h3 className='fw-bold m-0'>External API Audit Log</h3>
                <span className='text-muted fs-7 ms-3'>(last 7 days)</span>
              </div>
              <div className='card-toolbar'>
                <div className='d-flex align-items-center flex-wrap gap-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted fs-7 fw-bold me-2'>วันที่</span>
                    <DateRangePicker
                      className='w-250px'
                      from={dateFrom}
                      to={dateTo}
                      maxDate={new Date()}
                      onChange={onDateRangeChange}
                    />
                  </div>

                  <select
                    className='form-select form-select-solid w-150px'
                    value={statusFilter}
                    onChange={e => onStatusFilterChange(e.target.value)}>
                    <option value=''>สถานะทั้งหมด</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>

                  <div className='d-flex align-items-center position-relative'>
                    <KTSVG
                      path='/media/icons/duotune/general/gen021.svg'
                      className='svg-icon-1 position-absolute ms-4'
                    />
                    <input
                      type='text'
                      className='form-control form-control-solid w-250px ps-12 pe-10'
                      placeholder='ค้นหา'
                      value={search}
                      onChange={e => onSearchChange(e.target.value)}
                    />
                    {search && (
                      <button
                        type='button'
                        className='btn btn-icon btn-sm position-absolute end-0 me-2'
                        onClick={() => onSearchChange('')}
                        aria-label='Clear search'>
                        <KTSVG
                          path='/media/icons/duotune/arrows/arr061.svg'
                          className='svg-icon-2'
                        />
                      </button>
                    )}
                  </div>

                  <button
                    type='button'
                    className='btn btn-light-danger btn-sm'
                    onClick={clearFilters}>
                    ล้างตัวกรอง
                  </button>
                </div>
              </div>
            </div>

            <div className='card-body'>
              <div className='position-relative'>
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                  <thead>
                    <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                      <th style={{ width: 40 }}></th>
                      {renderSortableHeader('createdAt', 'Created At')}
                      {renderSortableHeader('endpoint', 'Endpoint')}
                      {renderSortableHeader('status', 'Status')}
                      {renderSortableHeader('errorMessage', 'Reason')}
                    </tr>
                  </thead>
                  <tbody className='text-gray-800'>
                    {pagedData.length > 0 ? (
                      pagedData.map((log, i) => (
                        <React.Fragment key={log.id}>
                          <tr className={clsx({ 'bg-gray-100': i % 2 === 0 })}>
                            <td>
                              <button
                                type='button'
                                className='btn btn-icon btn-sm btn-light-primary'
                                onClick={() => toggleExpand(log.id)}
                                aria-label='Toggle detail'>
                                <KTSVG
                                  path={
                                    isExpanded(log.id)
                                      ? '/media/icons/duotune/arrows/arr073.svg'
                                      : '/media/icons/duotune/arrows/arr072.svg'
                                  }
                                  className='svg-icon-2'
                                />
                              </button>
                            </td>
                            <td className='text-nowrap'>
                              <Highlight text={formatDate(log.createdAt)} term={search} />
                            </td>
                            <td>
                              <Highlight text={log.endpoint} term={search} />
                            </td>
                            <td>
                              <span
                                className={`badge fw-bold ${
                                  log.status === 'Success'
                                    ? 'badge-light-success'
                                    : 'badge-light-danger'
                                }`}>
                                <Highlight text={log.status} term={search} />
                              </span>
                            </td>
                            <td>
                              <Highlight text={log.errorMessage ?? '-'} term={search} />
                            </td>
                          </tr>
                          {isExpanded(log.id) && (
                            <tr className={clsx({ 'bg-gray-100': i % 2 === 0 })}>
                              <td colSpan={COLUMN_COUNT}>
                                <div className='p-4'>
                                  {log.errorMessage && (
                                    <div className='mb-4'>
                                      <div className='fw-bold text-danger mb-1'>Error Message</div>
                                      <div className='text-gray-800'>
                                        <Highlight text={log.errorMessage} term={search} />
                                      </div>
                                    </div>
                                  )}
                                  <div className='row'>
                                    <div className='col-12 col-lg-6 mb-4'>
                                      <div className='d-flex align-items-center justify-content-between mb-1'>
                                        <div className='fw-bold text-muted'>Request Body</div>
                                        <CopyButton value={prettyJson(log.requestBody)} />
                                      </div>
                                      <pre className='bg-white border rounded p-3 mb-0'>
                                        <Highlight text={prettyJson(log.requestBody)} term={search} />
                                      </pre>
                                    </div>
                                    <div className='col-12 col-lg-6 mb-4'>
                                      <div className='d-flex align-items-center justify-content-between mb-1'>
                                        <div className='fw-bold text-muted'>Response Body</div>
                                        <CopyButton value={prettyJson(log.responseBody)} />
                                      </div>
                                      <pre className='bg-white border rounded p-3 mb-0'>
                                        <Highlight text={prettyJson(log.responseBody)} term={search} />
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={COLUMN_COUNT}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center py-6'>
                            {isLoading
                              ? 'Loading...'
                              : isError
                              ? 'เกิดข้อผิดพลาดในการดึงข้อมูล'
                              : 'No matching records found'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
                {isLoading && <TableLoading />}
              </div>

              {filteredCount > pageSize && (
                <CustomTablePagination
                  isLoading={isLoading}
                  totalCountData={filteredCount}
                  page={page}
                  items_per_page={pageSize}
                  updatePage={goToPage}
                  links={[]}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ViewPageTitle>
  )
}

const ViewPageTitle: React.FC<WithChildren> = ({ children }) => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            isActive: true,
            isSeparator: false,
            path: '#',
            title: 'External API Audit Log',
            icon: '/media/icons/duotune/general/gen005.svg',
          },
        ]}>
        External API Audit Log
      </PageTitle>

      {children}
    </>
  )
}

export default ExternalApiAuditLogListView
